/**
 * ReactivarClinicaModal · PR-FASE-A-3 (Ann 2026-05-27)
 *
 * Modal de acción administrativa consciente para reactivar a un cliente
 * en el circuito clínico/fisioterapia tras una baja previa. Llama a la
 * autopista oficial POST /api/admin/clients/{cliente_id}/reactivar-clinica
 * (PR #243).
 *
 * Decisiones funcionales (Ann):
 *  - Motivo OPCIONAL · si se envía, catálogo cerrado validado.
 *  - "Sin especificar" → payload NO incluye motivo (backend usa default
 *    interno "reactivacion").
 *  - Si motivo='otro' → observaciones obligatorias.
 *  - Info-box con la baja anterior (fecha + motivo + observaciones) · DF2.
 *  - Si 409 CLIENTE_BAJA_CRM_TOTAL · modal NO cierra · mensaje claro.
 *  - Si 409 CLIENTE_EN_IS_ACTIVA_CLINICA · modal NO cierra · enlace IS.
 *  - Tras éxito · cerrar modal + toast verde + onSuccess() (refresco ficha).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { UserCheck, AlertTriangle, Info, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../../api';
import { MOTIVOS_BAJA_CLINICA, extractErrorPayload } from './BajaClinicaModal';

// Catálogo cerrado · valor backend ↔ texto UI (DF8). Mantiene "" como
// "Sin especificar" · si se envía vacío no se incluye en payload (default
// interno backend "reactivacion").
export const MOTIVOS_REACTIVACION = [
  { value: '',                     label: 'Sin especificar' },
  { value: 'recuperacion',         label: 'Recuperación' },
  { value: 'nueva_consulta',       label: 'Nueva consulta' },
  { value: 'seguimiento',          label: 'Seguimiento' },
  { value: 'error_administrativo', label: 'Error administrativo' },
  { value: 'otro',                 label: 'Otro' },
];

const MAX_OBSERVACIONES_LEN = 500;

export function traducirErrorReactivacion(error) {
  const { code, status } = extractErrorPayload(error);
  if (status === 403) return 'No tienes permiso para reactivar fisioterapia.';
  if (!error?.response) return 'Sin conexión con el servidor. Inténtalo de nuevo.';
  const map = {
    CLIENTE_NO_EXISTE:          'Cliente no encontrado. Recarga la página.',
    NO_ESTA_EN_BAJA_CLINICA:    'Este cliente no está dado de baja en fisioterapia. Recarga la ficha.',
    MOTIVO_INVALIDO:            'Motivo no válido. Recarga la página.',
    OBSERVACIONES_OBLIGATORIAS_SI_OTRO: "Las observaciones son obligatorias cuando el motivo es 'Otro'.",
    OBSERVACIONES_DEMASIADO_LARGAS: `Observaciones demasiado largas (máximo ${MAX_OBSERVACIONES_LEN} caracteres).`,
    OBSERVACIONES_TIPO_INVALIDO:'Observaciones deben ser texto.',
    FECHA_INVALIDA:             'Fecha inválida. Usa el selector de fecha.',
    SEDE_CLINICA_INVALIDA:      'Sede no válida. Recarga la página.',
    POSTCONDICION_FALLIDA:      'Error inesperado en la operación. Datos no modificados. Contacta con el administrador.',
    EXCEPCION_NO_CONTROLADA:    'Error interno del servidor. Datos no modificados. Contacta con el administrador.',
  };
  return map[code] || 'Error al reactivar. Inténtalo de nuevo.';
}

const HOY_ISO = () => new Date().toISOString().split('T')[0];

// Resuelve el texto del motivo de baja previo (intentar mapear al catálogo
// de baja para mostrar texto natural; si no hay match, mostrar el código).
function motivoBajaLabel(code) {
  if (!code) return null;
  const found = MOTIVOS_BAJA_CLINICA.find((m) => m.value === code);
  return found ? found.label : code;
}

const ReactivarClinicaModal = ({
  isOpen, onClose, clientId, clientName, onSuccess,
  fechaBajaPrevia, motivoBajaPrevio, observacionesBajaPrevia,
}) => {
  const [fechaAlta, setFechaAlta] = useState(HOY_ISO());
  const [motivo, setMotivo] = useState(''); // '' = sin especificar
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorInline, setErrorInline] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFechaAlta(HOY_ISO());
      setMotivo('');
      setObservaciones('');
      setSubmitting(false);
      setErrorInline(null);
    }
  }, [isOpen]);

  const observacionesObligatorias = motivo === 'otro';
  const observacionesValidas = !observacionesObligatorias || observaciones.trim().length > 0;
  const observacionesLongitudOk = observaciones.length <= MAX_OBSERVACIONES_LEN;
  const fechaValida = !!fechaAlta;  // backend acepta también fechas pasadas
  // motivo válido: vacío (sin especificar) o valor del catálogo
  const motivoValido = motivo === '' || MOTIVOS_REACTIVACION.some((m) => m.value === motivo);

  const canSubmit = !submitting && motivoValido && observacionesValidas && observacionesLongitudOk && fechaValida;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorInline(null);

    const payload = { fecha_alta_clinica: fechaAlta };
    if (motivo) payload.motivo_reactivacion_clinica = motivo;
    if (observaciones.trim()) {
      payload.observaciones_reactivacion_clinica = observaciones.trim();
    }

    try {
      await axios.post(`${API}/admin/clients/${clientId}/reactivar-clinica`, payload);
      toast.success(`Fisioterapia reactivada · ${fechaAlta}`);
      onSuccess?.();
      onClose();
    } catch (error) {
      const { code } = extractErrorPayload(error);
      if (code === 'CLIENTE_BAJA_CRM_TOTAL') {
        setErrorInline({ type: 'baja_crm_total' });
      } else if (code === 'CLIENTE_EN_IS_ACTIVA_CLINICA') {
        setErrorInline({ type: 'is_activa' });
      } else {
        toast.error(traducirErrorReactivacion(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const irAIncidencias = () => {
    try {
      const target = `/admin?tab=incidencias-salud&cliente_id=${encodeURIComponent(clientId || '')}`;
      window.open(target, '_blank', 'noopener');
    } catch (e) { /* sin acción */ }
  };

  const motivoLabel = motivoBajaLabel(motivoBajaPrevio);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-700">
            <UserCheck className="w-5 h-5" />
            Reactivar fisioterapia
            {clientName && <span className="text-gray-600 font-normal">· {clientName}</span>}
          </DialogTitle>
          <DialogDescription>
            Esta acción reabre el circuito clínico. La baja anterior queda registrada
            en el historial. No afecta a otros servicios.
          </DialogDescription>
        </DialogHeader>

        {/* Panel inline · CLIENTE_BAJA_CRM_TOTAL */}
        {errorInline?.type === 'baja_crm_total' && (
          <div className="border border-red-300 bg-red-50 rounded p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-red-800 mb-2">
              <AlertTriangle className="w-4 h-4" /> Cliente dado de baja del CRM
            </div>
            <div className="text-red-900">
              El cliente está en baja CRM total. Reactiva el cliente globalmente
              antes de reactivar solo el circuito clínico.
            </div>
            <div className="mt-3">
              <Button variant="ghost" size="sm" onClick={handleClose}>Cerrar</Button>
            </div>
          </div>
        )}

        {/* Panel inline · CLIENTE_EN_IS_ACTIVA_CLINICA */}
        {errorInline?.type === 'is_activa' && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2">
              <AlertTriangle className="w-4 h-4" /> Acción bloqueada
            </div>
            <div className="text-amber-900">
              El cliente tiene una incidencia de salud / pausa clínica activa.
              Resuelve o cierra la pausa clínica antes de reactivar.
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={irAIncidencias}>
                Ir a Incidencias de Salud <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClose}>Cerrar</Button>
            </div>
          </div>
        )}

        {/* Info-box · ciclo de baja anterior (DF2) */}
        {!errorInline && (fechaBajaPrevia || motivoLabel || observacionesBajaPrevia) && (
          <div className="border border-blue-200 bg-blue-50 rounded p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-blue-800 mb-1">
              <Info className="w-4 h-4" /> Ciclo de baja anterior
            </div>
            <ul className="text-blue-900 space-y-0.5">
              {fechaBajaPrevia && (
                <li><strong>Fecha de baja:</strong> {String(fechaBajaPrevia).slice(0, 10)}</li>
              )}
              {motivoLabel && (
                <li><strong>Motivo:</strong> {motivoLabel}</li>
              )}
              {observacionesBajaPrevia && (
                <li><strong>Observaciones:</strong> {observacionesBajaPrevia}</li>
              )}
            </ul>
          </div>
        )}

        {/* Formulario */}
        {!errorInline && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fecha-alta-clinica-reactivacion">Fecha de reactivación</Label>
              <Input
                id="fecha-alta-clinica-reactivacion"
                type="date"
                value={fechaAlta}
                onChange={(e) => setFechaAlta(e.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="motivo-reactivacion-clinica">Motivo (opcional)</Label>
              <select
                id="motivo-reactivacion-clinica"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                disabled={submitting}
              >
                {MOTIVOS_REACTIVACION.map((m) => (
                  <option key={m.value || 'sin'} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="observaciones-reactivacion-clinica">
                Observaciones {observacionesObligatorias ? '*' : '(opcional)'}
              </Label>
              <Textarea
                id="observaciones-reactivacion-clinica"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                disabled={submitting}
                rows={3}
                placeholder={observacionesObligatorias
                  ? "Obligatorio cuando el motivo es 'Otro'"
                  : 'Información adicional (opcional)'}
              />
              <div className="flex justify-between text-xs">
                {observacionesObligatorias && observaciones.trim().length === 0 ? (
                  <span className="text-red-600">Obligatorio para motivo &apos;Otro&apos;</span>
                ) : <span />}
                <span className={observacionesLongitudOk ? 'text-gray-500' : 'text-red-600'}>
                  {observaciones.length}/{MAX_OBSERVACIONES_LEN}
                </span>
              </div>
            </div>
          </div>
        )}

        {!errorInline && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando…</>
              ) : (
                <><UserCheck className="w-4 h-4 mr-1" /> Reactivar</>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReactivarClinicaModal;
